import { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import AnchorLink from '@/components/AnchorLink';
import Button from '@/components/Button';
import { useQuote } from '@/components/QuoteDialog';
import { NAV_LINKS } from '@/data/site';

const FOCUSABLE = 'a[href], button:not([disabled])';

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const { openQuote } = useQuote();
  const headerRef = useRef(null);
  const toggleRef = useRef(null);
  const solid = scrolled || open || pathname !== '/';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Otvoren meni: zaključan scroll, Esc zatvara, Tab ostaje unutar menija.
  useEffect(() => {
    if (!open) return undefined;
    document.body.style.overflow = 'hidden';

    const onKey = (e) => {
      if (e.key === 'Escape') {
        setOpen(false);
        toggleRef.current?.focus();
      } else if (e.key === 'Tab') {
        const items = headerRef.current?.querySelectorAll(FOCUSABLE);
        if (!items?.length) return;
        const first = items[0];
        const last = items[items.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    const onResize = () => window.innerWidth >= 1024 && setOpen(false);

    window.addEventListener('keydown', onKey);
    window.addEventListener('resize', onResize);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onResize);
    };
  }, [open]);

  const close = () => setOpen(false);

  return (
    <header
      ref={headerRef}
      data-testid="site-header"
      className={`on-dark fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        solid ? 'bg-ink border-b border-white/10' : 'bg-transparent'
      }`}
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-8 h-20 flex items-center justify-between">
        <AnchorLink href="#hero" data-testid="logo-link" onClick={close} className="flex items-center">
          <img src="/garmond_logo.png" alt="Garmond Graphic — početna" className="h-10 sm:h-11 w-auto" />
        </AnchorLink>

        <nav aria-label="Glavna navigacija" className="hidden lg:flex items-center gap-9" data-testid="desktop-nav">
          {NAV_LINKS.map((link) => (
            <AnchorLink
              key={link.href}
              href={link.href}
              data-testid={`nav-${link.href.slice(1)}`}
              className="text-sm font-medium text-white/80 hover:text-white transition-colors"
            >
              {link.label}
            </AnchorLink>
          ))}
          <Button onClick={() => openQuote()} data-testid="nav-quote-cta" className="!py-3 !px-5">
            Zatražite ponudu
          </Button>
        </nav>

        <button
          ref={toggleRef}
          type="button"
          data-testid="mobile-menu-toggle"
          aria-expanded={open}
          aria-controls="mobile-nav"
          aria-label={open ? 'Zatvori meni' : 'Otvori meni'}
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden -mr-2 p-2 text-white"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      {open && (
        <nav
          id="mobile-nav"
          aria-label="Mobilna navigacija"
          data-testid="mobile-nav"
          className="lg:hidden fixed inset-x-0 top-20 bottom-0 bg-ink overflow-y-auto px-6 pb-10"
        >
          <ul className="border-t border-white/10">
            {NAV_LINKS.map((link) => (
              <li key={link.href} className="border-b border-white/10">
                <AnchorLink
                  href={link.href}
                  onClick={close}
                  data-testid={`mobile-nav-${link.href.slice(1)}`}
                  className="block py-5 text-2xl text-white hover:text-white/70"
                  style={{ fontFamily: "'Archivo Black', 'Outfit', sans-serif" }}
                >
                  {link.label}
                </AnchorLink>
              </li>
            ))}
          </ul>
          <Button onClick={() => { close(); openQuote(); }} className="mt-8 w-full">
            Zatražite ponudu
          </Button>
        </nav>
      )}
    </header>
  );
}
